/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 */
 const currencySymbol = window.currency.symbol || '$';
 const moneyFormat = currencySymbol+'{{amount}}';

 /**
  * Format money values based on your shop currency settings
  * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
  * or 3.00 dollars
  * @param  {String} format - shop money_format setting
  * @return {String} value - formatted value
  */
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true })

    // Available package sizes
    this.packageSizes = this.dataset.packageSizes;
    this.packageSizesArray = this.packageSizes.split('|').map(Number).sort((a, b) => a - b);

    // Set initial package size
    this.currentPackageSize = this.packageSizesArray[0];

    // Set the initial / current package size
    this.packageSizesArray.forEach((size) => {
      if (parseInt(this.querySelector('[data-cart-quantity-value]').value) >= size) {
        this.currentPackageSize = size;
      }
    });

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const trigger = event.currentTarget;
    const wrapper = this;

    // Find quantity input
    const quantityInput = wrapper.querySelector('[data-cart-quantity-value]');

    // Get quantities
    const currentValue = parseInt(quantityInput.value);
    const minQuantity = parseInt(quantityInput.getAttribute('min'))
    const maxQuantity = parseInt(quantityInput.getAttribute('max'));

    // Update the minimum quantity to the most appropriate one according to the current quantity
    const minusButton = trigger.classList.contains('quantity__button--minus');
    const plusButton = trigger.classList.contains('quantity__button--plus');

    const getNewValue = (currentValue, operation) => {
      let newValue;

      // When value greater than this, increase the package size to this
      const maxValuePackageThreshold = 1000;
      const maxValuePackageSize = 100;

      // Find position in array and extrapolate prev and next sizes
      const i = this.packageSizesArray.indexOf(this.currentPackageSize);
      const prevSize = this.packageSizesArray[i - 1];
      const size = this.packageSizesArray[i];
      const nextSize = this.packageSizesArray[i + 1];

      if (operation === '+') {
        newValue = currentValue + (currentValue + this.currentPackageSize <= maxValuePackageThreshold ? this.currentPackageSize : maxValuePackageSize);

        if (nextSize && currentValue >= size) {
          if (currentValue < nextSize) {
            this.currentPackageSize = size;
            newValue = currentValue + this.currentPackageSize;

            if (newValue > nextSize) {
              this.currentPackageSize = nextSize;
              newValue = nextSize;
            }
          } else {
            this.currentPackageSize = nextSize;
            newValue = currentValue + this.currentPackageSize;
          }
        }
      }

      if (operation === '-') {

        newValue = currentValue - (currentValue - this.currentPackageSize <= maxValuePackageThreshold ? this.currentPackageSize : maxValuePackageSize);

        if (prevSize && newValue < size) {
          this.currentPackageSize = prevSize;

          const remainder = size % prevSize;
          newValue = size - remainder;
        }
      }

      this.currentPackageSize = this.currentPackageSize <= maxValuePackageThreshold ? this.currentPackageSize : maxValuePackageSize;

      newValue = (newValue < minQuantity) ? minQuantity : newValue ;

      return newValue;
    };

    const operation = minusButton ? '-' : '+';
    let newQuantity = getNewValue(currentValue, operation);

    // if nothing changed or value is 0
    if (newQuantity === currentValue || newQuantity <= 0) return;

    // Check: if maximum quantity is reached
    if (newQuantity > maxQuantity) {
      newQuantity = maxQuantity;
      debug();
      trigger.setAttribute('disabled', true);
    } else {
      debug();
      quantityInput.value = newQuantity;

      trigger.removeAttribute('disabled');
      quantityInput.dispatchEvent(this.changeEvent);
    }
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}


function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

const serializeForm = form => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

const debug = () => {
  return window.location.hostname === '127.0.0.1' || window.location.hostname.indexOf('myshopify.com') !== -1;
}

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt_select = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt_select.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};
